// Role utilities for frontend components
// Provides consistent role handling across the application

// Mapping of corrupted/malformed storage keys to correct role names
const ROLE_KEY_MAPPING = {
  // Common corrupted role keys that might exist in storage
  'user': 'User',
  'USER': 'User',
  'admin': 'admin',
  'ADMIN': 'admin',
  'system-admin': 'system-admin',
  'SYSTEM-ADMIN': 'system-admin',
  'systemAdmin': 'system-admin',
  'SystemAdmin': 'system-admin',
  'signed-in': 'signed-in',
  'signedIn': 'signed-in',
  'SignedIn': 'signed-in',
  'SIGNED-IN': 'signed-in',
  'unauthenticated': 'unauthenticated',
  'UNAUTHENTICATED': 'unauthenticated',
  'unauth': 'unauthenticated',
  'anonymous': 'unauthenticated',
  // Handle potential corrupted entries
  '': null,
  'null': null,
  'undefined': null,
  'false': null,
  'true': null
};

// Valid roles in the system
const VALID_ROLES = [
  'User',
  'admin',
  'signed-in',
  'system-admin',
  'unauthenticated'
];

/**
 * Normalizes a single role key to its correct form
 * @param {string|null|undefined} roleKey - The role key to normalize
 * @returns {string|null} - The normalized role key or null if invalid
 */
export function normalizeRoleKey(roleKey) {
  if (!roleKey || typeof roleKey !== 'string') {
    return null;
  }

  // Trim whitespace and handle empty strings
  const trimmed = roleKey.trim();
  if (!trimmed) {
    return null;
  }

  // Check if it's already a valid role
  if (VALID_ROLES.includes(trimmed)) {
    return trimmed;
  }

  // Check the mapping for corrupted keys
  if (ROLE_KEY_MAPPING.hasOwnProperty(trimmed)) {
    return ROLE_KEY_MAPPING[trimmed];
  }

  // Try lowercase version
  const lowercase = trimmed.toLowerCase();
  if (ROLE_KEY_MAPPING.hasOwnProperty(lowercase)) {
    return ROLE_KEY_MAPPING[lowercase];
  }

  // If no mapping found, return null for invalid roles
  return null;
}

/**
 * Normalizes roles from various formats (string arrays, role objects, etc.)
 * @param {Array|Object|string|null|undefined} roles - The roles to normalize
 * @returns {Array<string>} - Array of normalized role strings
 */
export function normalizeRoles(roles) {
  if (!roles) {
    return [];
  }

  let roleArray = [];

  // Handle string (single role)
  if (typeof roles === 'string') {
    const normalized = normalizeRoleKey(roles);
    return normalized ? [normalized] : [];
  }

  // Handle array of roles
  if (Array.isArray(roles)) {
    roleArray = roles;
  } 
  // Handle role objects (like from Gadget's roleList field)
  else if (typeof roles === 'object') {
    // Check if it's a role object with a key property
    if (roles.key) {
      roleArray = [roles.key];
    }
    // Check if it's an array-like object or has role properties
    else if (roles.roles && Array.isArray(roles.roles)) {
      roleArray = roles.roles;
    }
    // Try to extract role keys from object
    else {
      roleArray = Object.keys(roles).filter(key => 
        roles[key] === true || VALID_ROLES.includes(key)
      );
    }
  }

  // Normalize each role in the array
  const normalizedRoles = roleArray
    .map(role => {
      // Handle role objects in array
      if (typeof role === 'object' && role !== null) {
        // IMPORTANT: Check 'name' property first, then fall back to 'key'
        // Gadget assigns random keys to custom roles (like 'CBZwH11qmg2O' for 'User')
        // but the 'name' property contains the actual role name we care about
        const roleName = role.name || role.key || role.role;
        return roleName;
      }
      return role;
    })
    .map(normalizeRoleKey)
    .filter(role => role !== null);

  // Remove duplicates and return
  return [...new Set(normalizedRoles)];
}

/**
 * Checks if a role key is valid
 * @param {string} roleKey - The role key to validate
 * @returns {boolean} - True if the role is valid
 */
export function isValidRole(roleKey) {
  if (!roleKey || typeof roleKey !== 'string') {
    return false;
  }

  return VALID_ROLES.includes(roleKey.trim());
}

/**
 * Gets all valid roles in the system
 * @returns {Array<string>} - Array of all valid role names
 */
export function getValidRoles() {
  return [...VALID_ROLES];
}

/**
 * Checks if a user has a specific role
 * @param {Array|Object|string} userRoles - The user's roles
 * @param {string} targetRole - The role to check for
 * @returns {boolean} - True if the user has the target role
 */
export function hasRole(userRoles, targetRole) {
  if (!targetRole || !isValidRole(targetRole)) {
    return false;
  }

  const normalizedRoles = normalizeRoles(userRoles);
  return normalizedRoles.includes(targetRole);
}

/**
 * Checks if a user has any of the specified roles
 * @param {Array|Object|string} userRoles - The user's roles
 * @param {Array<string>} targetRoles - The roles to check for
 * @returns {boolean} - True if the user has any of the target roles
 */
export function hasAnyRole(userRoles, targetRoles) {
  if (!Array.isArray(targetRoles) || targetRoles.length === 0) {
    return false;
  }

  const normalizedUserRoles = normalizeRoles(userRoles);
  return targetRoles.some(role => normalizedUserRoles.includes(role));
}

/**
 * Checks if a user has all of the specified roles
 * @param {Array|Object|string} userRoles - The user's roles
 * @param {Array<string>} targetRoles - The roles to check for
 * @returns {boolean} - True if the user has all of the target roles
 */
export function hasAllRoles(userRoles, targetRoles) {
  if (!Array.isArray(targetRoles) || targetRoles.length === 0) {
    return false;
  }

  const normalizedUserRoles = normalizeRoles(userRoles);
  return targetRoles.every(role => normalizedUserRoles.includes(role));
}

/**
 * Gets the primary role for a user
 * @param {Object} user - The user object
 * @returns {string|null} - The primary role or null if no role found
 */
export function getUserRole(user) {
  if (!user || typeof user !== 'object') {
    return null;
  }

  // Try to get roles from user.roles or user.roleList
  const userRoles = user.roles || user.roleList;
  const normalizedRoles = normalizeRoles(userRoles);

  if (normalizedRoles.length === 0) {
    return null;
  }

  // Return the first normalized role as primary
  return normalizedRoles[0];
}

/**
 * Checks if user can access a specific shift
 * @param {Object} user - The user object
 * @param {Object} shift - The shift object
 * @returns {boolean} - True if user can access the shift
 */
export function canUserAccessShift(user, shift) {
  if (!user || typeof user !== 'object') {
    return false;
  }

  // Admin and system-admin can access all shifts
  if (hasAnyRole(user.roles || user.roleList, ['admin', 'system-admin'])) {
    return true;
  }

  // If no shift provided, default to false for safety
  if (!shift || typeof shift !== 'object') {
    return false;
  }

  // Check if user is assigned to the shift
  if (shift.assignedUserId && user.id && shift.assignedUserId === user.id) {
    return true;
  }

  // Check if user is the instructor for the shift
  if (shift.instructorId && user.id && shift.instructorId === user.id) {
    return true;
  }

  // Instructors can generally access shifts
  if (isInstructor(user)) {
    return true;
  }

  // Schedulers can access shifts for scheduling purposes
  if (isScheduler(user)) {
    return true;
  }

  return false;
}

/**
 * Check if a user can generate scenarios (frontend version)
 * 
 * This is a synchronous client-side check that examines the user object directly.
 * It checks:
 * 1. Individual scenario access flag
 * 2. Cohort membership scenario access
 * 3. Admin/system-admin roles (always have access)
 * 
 * Note: This does NOT check institution-wide settings or student eval viewing.
 * For complete server-side access checks, use the backend canUserGenerateScenarios from api/lib/userAccessChecks.js
 * 
 * @param {Object} user - The user object with roles, memberships, etc.
 * @returns {Object} Access information with:
 *   - hasAccess: boolean - Can generate scenarios
 *   - canViewEvals: boolean - Can view evaluations (always false on frontend)
 *   - canSeeSharedScenarios: boolean - Can see institution's shared scenarios
 *   - source: string|null - Access source: 'individual', 'cohort', 'admin', or null
 *   - institution: string|null - Institution name if applicable
 */
export function canUserGenerateScenarios(user) {
  if (!user || typeof user !== 'object') {
    return {
      hasAccess: false,
      canViewEvals: false,
      canSeeSharedScenarios: false,
      source: null,
      institution: null,
    };
  }

  // Check if user is admin or system-admin (they always have access)
  if (hasAnyRole(user.roles || user.roleList, ['admin', 'system-admin'])) {
    return {
      hasAccess: true,
      canViewEvals: false,
      canSeeSharedScenarios: true,
      source: 'admin',
      institution: user.institution || null,
    };
  }

  // Check cohort membership scenario access
  if (user.cohortMemberships) {
    // Handle both array format and edges format
    let memberships = [];
    if (Array.isArray(user.cohortMemberships)) {
      memberships = user.cohortMemberships;
    } else if (user.cohortMemberships.edges) {
      memberships = user.cohortMemberships.edges.map(edge => edge.node);
    }

    for (const membership of memberships) {
      if (
        membership.active &&
        membership.hasScenarioGeneratorAccess === true &&
        membership.cohort
      ) {
        return {
          hasAccess: true,
          canViewEvals: false,
          canSeeSharedScenarios: true,
          source: 'cohort',
          institution: membership.cohort.institution || null,
        };
      }

      // Check if cohort grants scenario access to instructors
      if (
        membership.active &&
        membership.primaryRole === 'instructor' &&
        membership.cohort &&
        membership.cohort.grantScenarioAccessToInstructors === true
      ) {
        return {
          hasAccess: true,
          canViewEvals: false,
          canSeeSharedScenarios: true,
          source: 'cohort',
          institution: membership.cohort.institution || null,
        };
      }
    }
  }

  // Check individual scenario access
  if (user.individualScenarioAccess === true) {
    return {
      hasAccess: true,
      canViewEvals: false,
      canSeeSharedScenarios: false,
      source: 'individual',
      institution: null,
    };
  }

  // No access found
  return {
    hasAccess: false,
    canViewEvals: false,
    canSeeSharedScenarios: false,
    source: null,
    institution: null,
  };
}

/**
 * Checks if user is an instructor
 * @param {Object} user - The user object
 * @returns {boolean} - True if user is an instructor
 */
export function isInstructor(user) {
  if (!user || typeof user !== 'object') {
    return false;
  }

  // Admin and system-admin have instructor privileges
  if (hasAnyRole(user.roles || user.roleList, ['admin', 'system-admin'])) {
    return true;
  }

  // Check institutionRole field
  if (user.institutionRole) {
    const role = typeof user.institutionRole === 'string' 
      ? user.institutionRole.toLowerCase() 
      : '';
    if (role === 'instructor' || role === 'teacher' || role === 'faculty') {
      return true;
    }
  }

  // Check if user has instructor-related roles
  const userRoles = normalizeRoles(user.roles || user.roleList);
  const instructorRoles = ['instructor', 'teacher', 'faculty'];
  return userRoles.some(role => 
    instructorRoles.includes(role.toLowerCase())
  );
}

/**
 * Checks if user is a scheduler
 * @param {Object} user - The user object
 * @returns {boolean} - True if user is a scheduler
 */
export function isScheduler(user) {
  if (!user || typeof user !== 'object') {
    return false;
  }

  // Admin and system-admin have scheduler privileges
  if (hasAnyRole(user.roles || user.roleList, ['admin', 'system-admin'])) {
    return true;
  }

  // Check for explicit scheduler roles
  const userRoles = normalizeRoles(user.roles || user.roleList);
  const schedulerRoles = ['scheduler', 'coordinator', 'manager'];
  return userRoles.some(role => 
    schedulerRoles.includes(role.toLowerCase())
  );
}

/**
 * Checks if user is a student
 * @param {Object} user - The user object
 * @returns {boolean} - True if user is a student
 */
export function isStudent(user) {
  if (!user || typeof user !== 'object') {
    return false;
  }

  // Admin and system-admin are not typically students in this context
  // but they might need student access for testing/demo purposes
  
  // Check institutionRole field first
  if (user.institutionRole) {
    const role = typeof user.institutionRole === 'string' 
      ? user.institutionRole.toLowerCase() 
      : '';
    if (role === 'student' || role === 'learner') {
      return true;
    }
  }

  // Check if user has student-related roles
  const userRoles = normalizeRoles(user.roles || user.roleList);
  const studentRoles = ['student', 'learner'];
  
  // If user has student roles, they're a student
  if (userRoles.some(role => studentRoles.includes(role.toLowerCase()))) {
    return true;
  }

  // If user only has 'User' or 'signed-in' roles and no other specific roles,
  // they might be a default student
  const basicRoles = ['User', 'signed-in'];
  const hasOnlyBasicRoles = userRoles.length > 0 && 
    userRoles.every(role => basicRoles.includes(role));
  
  if (hasOnlyBasicRoles && !isInstructor(user) && !isScheduler(user)) {
    return true;
  }

  return false;
}